import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { ShieldAlert, Loader2 } from "lucide-react";
import {
  adminListAwareness,
  adminUpdateAwareness,
} from "@/lib/api/awareness.functions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/portal/admin/awareness")({
  head: () => ({
    meta: [
      { title: "Admin · Awareness bookings" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminAwarenessPage,
});

const STATUSES = ["requested", "confirmed", "completed", "declined", "cancelled"] as const;

function AdminAwarenessPage() {
  const qc = useQueryClient();
  const list = useServerFn(adminListAwareness);
  const update = useServerFn(adminUpdateAwareness);
  const { data } = useQuery({
    queryKey: ["admin-awareness"],
    queryFn: () => list(),
  });
  const [savingId, setSavingId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  async function patch(id: string, patchData: any) {
    setSavingId(id);
    try {
      await update({ data: { id, ...patchData } });
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["admin-awareness"] });
    } catch (err: any) {
      toast.error("Update failed", { description: err?.message });
    } finally {
      setSavingId(null);
    }
  }

  const bookings = data?.bookings ?? [];

  return (
    <div className="space-y-6">
      <header className="flex items-start gap-3">
        <ShieldAlert className="mt-1 h-5 w-5 text-gold" />
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin</p>
          <h2 className="font-display text-2xl text-navy">Crime-awareness bookings</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Requests from schools, churches, communities, events & corporates.
          </p>
        </div>
      </header>

      {bookings.length === 0 ? (
        <p className="text-sm text-muted-foreground">No requests yet.</p>
      ) : (
        <ul className="space-y-3">
          {bookings.map((b: any) => (
            <li
              key={b.id}
              className="rounded-2xl border border-border bg-card p-5 shadow-soft"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-navy">{b.organisation}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {b.organisation_type.replace("_", " ")} ·{" "}
                    {b.audience_age_group.replace("_", " ")} · {b.audience_size} pax
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {b.contact_name} — {b.contact_email}
                    {b.contact_phone ? ` · ${b.contact_phone}` : ""}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {b.city ? `${b.city}, ` : ""}{b.province}
                    {b.venue ? ` · ${b.venue}` : ""}
                  </p>
                  {(b.preferred_date || b.preferred_time) && (
                    <p className="text-xs text-muted-foreground">
                      Preferred: {b.preferred_date ?? "—"} {b.preferred_time}
                    </p>
                  )}
                </div>
                <div className="w-44">
                  <Select
                    value={b.status}
                    onValueChange={(v) => patch(b.id, { status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {b.topics && (
                <p className="mt-3 text-xs">
                  <span className="font-semibold text-navy">Topics: </span>
                  {b.topics}
                </p>
              )}
              {b.message && (
                <p className="mt-2 rounded-md bg-muted px-3 py-2 text-xs">
                  {b.message}
                </p>
              )}
              <div className="mt-3">
                <Textarea
                  defaultValue={b.admin_notes}
                  onChange={(e) =>
                    setNotes((n) => ({ ...n, [b.id]: e.target.value }))
                  }
                  placeholder="Internal notes…"
                  className="min-h-[60px] text-xs"
                />
                <Button
                  size="sm"
                  className="mt-2 bg-navy hover:bg-navy/90"
                  disabled={savingId === b.id}
                  onClick={() =>
                    patch(b.id, { admin_notes: notes[b.id] ?? b.admin_notes })
                  }
                >
                  {savingId === b.id ? (
                    <><Loader2 className="mr-2 h-3 w-3 animate-spin" /> Saving…</>
                  ) : (
                    "Save notes"
                  )}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
